import { useEffect, useId, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { focusRing, quietFill } from '@/components/ui/buttonStyles'
import { StatusBadge, StrengthBar, formatNextReview } from '@/components/vocabulary/VocabularyRow'
import { searchLearnerWords } from '@/api/admin'
import { cn } from '@/utils/cn'
import { Fact } from '../Fact'
import { spanLabel } from '../lab/labGeometry'
import { sinceLabel } from './adminDates'
import type { LabWord } from '@/types/admin'

interface LearnerWordsModalProps {
  userId: number
  languageId: number
  languageName: string
  onClose: () => void
}

/**
 * A learner's words in one language, searchable by lemma, and how strong each one
 * is. Read-only: an admin looks, the learner's schedule is never changed here.
 */
export function LearnerWordsModal({ userId, languageId, languageName, onClose }: LearnerWordsModalProps) {
  const [selected, setSelected] = useState<LabWord | null>(null)

  return (
    <Modal open onClose={onClose} title={`Words in ${languageName}`}>
      {selected ? (
        <WordStrength word={selected} onBack={() => setSelected(null)} />
      ) : (
        <WordSearch userId={userId} languageId={languageId} onPick={setSelected} />
      )}
    </Modal>
  )
}

interface WordSearchProps {
  userId: number
  languageId: number
  onPick: (word: LabWord) => void
}

function WordSearch({ userId, languageId, onPick }: WordSearchProps) {
  const inputId = useId()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<LabWord[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    setResults(null)
    const timer = setTimeout(() => {
      searchLearnerWords(userId, languageId, query)
        .then((res) => {
          setResults(res.data.data)
          setError(false)
        })
        .catch(() => setError(true))
    }, 180)
    return () => clearTimeout(timer)
  }, [userId, languageId, query])

  return (
    <div>
      <label htmlFor={inputId} className="font-mono text-xs uppercase tracking-widest text-muted">
        Search by word
      </label>
      <input
        id={inputId}
        type="search"
        autoComplete="off"
        spellCheck={false}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="mt-2 w-full min-h-11 rounded-md border border-border-divider bg-surface-bg px-4 py-3 font-serif text-base text-body placeholder:text-muted/60 transition-colors duration-200 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
      />
      <p className="mt-2 font-serif text-sm text-muted">
        {query.trim() ? 'Matching words, A to Z.' : 'Showing the words due soonest.'}
      </p>

      <div aria-live="polite" className="mt-4">
        {error ? (
          <p role="alert" className="font-serif text-sm text-muted">Could not search the words. Try again.</p>
        ) : results === null ? (
          <p className="flex items-center gap-2 font-serif text-sm text-muted">
            <Spinner />
            Searching…
          </p>
        ) : results.length === 0 ? (
          <p className="font-serif text-sm text-muted">No tracked word matches “{query}”.</p>
        ) : (
          <ul className="-mx-2 max-h-[22rem] overflow-y-auto">
            {results.map((word) => (
              <li key={word.id}>
                <button
                  type="button"
                  onClick={() => onPick(word)}
                  className={cn(
                    'flex w-full min-h-11 items-center justify-between gap-4 rounded-md px-2 py-2.5 text-left',
                    'transition-colors duration-200 hover:bg-surface-bg',
                    focusRing,
                  )}
                >
                  <span className="min-w-0 truncate">
                    <span className="font-serif text-base text-heading">{word.lemma}</span>
                    {word.gloss && <span className="ml-2 font-serif text-sm text-muted">{word.gloss}</span>}
                  </span>
                  <StrengthBar easeFactor={word.ease_factor} status={word.status} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function WordStrength({ word, onBack }: { word: LabWord; onBack: () => void }) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className={cn('inline-flex min-h-11 items-center rounded-full px-4 text-sm', quietFill, focusRing)}
      >
        &larr; All words
      </button>

      <div className="mt-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <p>
          <span className="font-serif text-2xl text-heading">{word.lemma}</span>
          {word.gloss && <span className="ml-3 font-serif text-base text-muted">{word.gloss}</span>}
        </p>
        <span className="flex items-center gap-3">
          <StatusBadge status={word.status} />
          <StrengthBar easeFactor={word.ease_factor} status={word.status} />
        </span>
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-line-soft pt-5">
        <Fact label="Next review" value={formatNextReview(word.next_review_at)} />
        <Fact label="Interval" value={spanLabel(word.interval_days)} />
        <Fact label="Ease" value={word.ease_factor.toFixed(2)} />
        <Fact label="Reviews" value={String(word.reviews_count)} />
        <Fact label="Last reviewed" value={sinceLabel(word.last_reviewed_at)} />
        <Fact
          label="Slip memory"
          value={word.lapsed_interval_days ? spanLabel(word.lapsed_interval_days) : 'none'}
        />
      </dl>
    </div>
  )
}
